import React, { useEffect, useState } from "react";
import Loader from "../components/common/loader/loader";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../constants/routes";
import Error from "../components/common/error/error";
import { notify } from "../lib/utils";
import TemplateSettingsForm from "../components/design/template-settings-dialog/template-settings-form";
import {
  getTemplateById,
  updateTemplateSettingsOnly,
} from "../api/admin/templates";
import { Button } from "../components/ui/button";
import usePageTitle from "../hooks/usePageTitle";

function TemplateSettingsFormPage() {
  const params = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<any>(null); // as loaded.

  const [templateSettings, setTemplateSettings] = useState<any>(null); // track form data.

  const [loading, setLoading] = React.useState(true);

  const [btnLoading, setBtnLoading] = React.useState(false); // when saving

  const [error, setError] = React.useState<any>(false);

  const [formHasErrors, setFormHasErrors] = React.useState<any>(false);

  usePageTitle("Template's Settings: " + template?.internalName);

  const getPropertiesForUpdate = (template: any) => {
    // get the properties that are required for update
    return {
      internalName: template.internalName,
      displayName: template.displayName,
      isPublished: template.isPublished,
      slug: template.slug,
      description: template.description,
      tags: template.tags || [],
      fitCategory: template.fitCategory,
      projectIds: template.projectIds,
      categorySelections: template.categorySelections,
      printLocations: template.printLocations,
      fetchOnlyWithinSets: template.fetchOnlyWithinSets,
      sharedCustomization: template.sharedCustomization,
    };
  };

  const loadTemplateById = async (id: string) => {
    // fetch the template by id
    const response = await getTemplateById(id);
    if (!response || response.status !== 200) {
      setError("Error loading template");
      setLoading(false);
      return;
    }

    setTemplate(response.data);
    setTemplateSettings(getPropertiesForUpdate(response.data));
    setError(null);
    setLoading(false);
  };

  useEffect(() => {
    if (params.id) {
      loadTemplateById(params.id);
    } else {
      setLoading(false);
      setError(true);
    }

    // reset the state when unmounting
    return () => {
      setTemplate(null);
      setLoading(false);
      setError(null);
    };
  }, []);

  const saveTemplate = async () => {
    if (formHasErrors) return;

    setBtnLoading(true);

    const response = await updateTemplateSettingsOnly(
      template._id,
      templateSettings
    );

    if (!response || response.status !== 200) {
      setBtnLoading(false);
      // check if slug duplication issue.
      if (response?.status === 409) {
        console.log("slug duplication issue");

        return notify({
          type: "error",
          message: "Template with this slug already exists!",
        });
      }

      return notify({
        type: "error",
        message: "Failed to save the template!",
      });
    }

    setBtnLoading(false);
    // navigate to the template page
    navigate(ROUTES.TEMPLATES);
  };

  return (
    <div className="flex w-full h-full">
      {loading && <Loader pageLoader />}

      {!loading && error && <Error message={error} />}

      {!loading && !error && (
        <div className="w-[95%] mx-auto flex flex-col  h-max">
          <div className="w-full mx-auto">
            <h1 className="text-2xl font-bold text-primary">
              Template's Settings: {template?.internalName}
            </h1>
          </div>

          <div className="w-full h-full mt-10 mb-20">
            <TemplateSettingsForm
              templateSettings={templateSettings}
              setTemplateSettings={setTemplateSettings}
              isForDialog={false}
              setFormHasErrors={setFormHasErrors}
            />

            <div className="flex mt-10 justify-end">
              <Button
                loading={btnLoading}
                disabled={formHasErrors}
                onClick={saveTemplate}
              >
                Save Template
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TemplateSettingsFormPage;
